import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Pagination, Stack, Typography } from '@mui/material'
import { Loader } from '@/components'
import { EmptyListData } from '@/components/ui'
import { useFilters, useInternalHoursLogs, useModal } from '@/hooks'
import { DEFAULT_LIST_LIMIT, DEFAULT_LIST_OFFSET } from '@/constants'
import {
  InternalHoursLog as InternalHoursLogType,
  PaginatedListResponse,
  SearchRequestParams,
} from '@/types'
import { InternalHoursLogsTable } from '@/features/internalHours/InternalHoursLogsTable'
import { InternalHoursLogModal } from '@/features/internalHours/InternalHoursLogModal'

export const InternalHoursLogsContainer = (): JSX.Element => {
  const { t } = useTranslation('internalHours')
  const { open } = useModal()

  const defaultFilterParams: SearchRequestParams = {
    limit: DEFAULT_LIST_LIMIT,
    offset: DEFAULT_LIST_OFFSET,
  }

  const {
    filterParams,
    initPagesCount,
    pagesCount,
    page,
    onPageChange,
    openDetail,
  } = useFilters<
    SearchRequestParams,
    PaginatedListResponse<InternalHoursLogType>,
    InternalHoursLogType
  >(defaultFilterParams)

  const { data, isLoading } = useInternalHoursLogs(filterParams)

  useEffect(() => {
    if (data?.count) {
      initPagesCount(data.count)
      const detailItemId = openDetail()

      if (detailItemId) openDetails(detailItemId)
    }
  }, [data])

  const openDetails = (id: number) => {
    if (!data?.results) return
    const item = data.results.find((r) => r.id === id)

    if (item) open(<InternalHoursLogModal logData={item} />)
  }

  const onCreate = () => open(<InternalHoursLogModal />)

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" fontWeight="bold">
          {t('logs.title')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography color="green.600">
            {t('logs.amount', { amount: data?.count ?? 0 })}
          </Typography>
          <Button variant="contained" color="primary" onClick={onCreate}>
            {t('logs.buttons.add')}
          </Button>
        </Stack>
      </Stack>

      {data?.count ? (
        <>
          <InternalHoursLogsTable
            rows={data.results}
            onOpenDetails={openDetails}
          />
          {pagesCount > 1 && (
            <Box alignSelf="center" mt={2}>
              <Pagination
                count={pagesCount}
                page={page}
                onChange={onPageChange}
              />
            </Box>
          )}
        </>
      ) : (
        <Box my={10}>
          {isLoading && <Loader />}
          {!isLoading && !data?.count && (
            <EmptyListData textKey="internalHours:logs.noData" />
          )}
        </Box>
      )}
    </Stack>
  )
}
